import { Button, Paper } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { SiTrackGrid } from '../../components/si-track-grid/si-track-grid';
import { customersApiKeyStore } from '../../api/customers-api-key-store';
import { Customer } from '../../models/customer';
import { columns } from './customers-grid-columns';
import { CustomGridCell } from './custom-grid-cell';
import { useNavigate } from 'react-router-dom';
import { SiTrackPage } from '../../components/si-track-page';
import { UserPermissionName } from '../../models/user-permission-name';
import { GuardedComponent } from '../../components/guarded-component';
import { useLoadData } from '../../hooks/useLoadData';
import { ApiResponse } from '../../models/api-response';
import { useCallback, useEffect, useRef, useState } from 'react';
import useDebounce from '../../hooks/use-debounce';
import { GridColumnConfig, GridPreference } from '../../models/gridCoumnConfig';
import { State } from '@progress/kendo-data-query';
import { useSaveData } from '../../hooks/useSaveData';
import { gridApiKeyStore } from '../../api/grid-api-key-store';
import { LoadingIndicator } from '../../components/loading-indicator.component';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '10px' },
};

export const CustomersPage = () => {
  const navigate = useNavigate();
  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('Customer', 'Customer'));
  const {
    isLoading,
    data: customer,
    refetch,
  } = useLoadData<ApiResponse<Customer[]>>(customersApiKeyStore.getCustomers());

  const handleCreateCustomer = () => {
    navigate('/customers/create');
  };

  const handleEditCustomer = (value: Customer) => {
    navigate(`/customers/edit/${value.customerID}`);
  };

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredCustomerList = customer?.data?.filter((adjuster) =>
      Object.values(adjuster).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredCustomers(filteredCustomerList || []);
  }, [debouncedSearchTerm, customer?.data]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'Customer',
      pageName: 'Customer',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'Customer',
      pageName: 'Customer',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  return (
    <div className="contenWrapper">
      <SiTrackPage
        title="Customers"
        hideBackNav
        actionButton={
          <GuardedComponent requiredPermissions={[UserPermissionName.ADD_CUSTOMER]}>
            <Button
              size="small"
              variant="contained"
              disableElevation={true}
              startIcon={<AddIcon />}
              onClick={handleCreateCustomer}
            >
              Add Customer
            </Button>
          </GuardedComponent>
        }
      >
        <div className="innerContent">
          <Paper elevation={0} style={styling.paperStyle}>
            <SiTrackGrid
              fileTitle='customer'
              columns={columns}
              dataItemKey="customerID"
              customCell={(field) =>
                CustomGridCell({
                  field,
                })
              }
              dataItems={filteredCustomers || []}
              // queryConfig={customersApiKeyStore.getCustomers()}
              viewRoute={(value) => `details/${value.customerID}`}
              onEdit={handleEditCustomer}
              actionPermissions={{
                edit: UserPermissionName.EDIT_CUSTOMER,
                delete: UserPermissionName.DELETE_CUSTOMER,
                export: UserPermissionName.DOWNLOAD_GRID,
              }}
              isBusy={isLoadingGridLayout || isLoading}
              deleteMutationConfig={(customer: Customer) => customersApiKeyStore.deleteCustomer(customer.customerID)}
              reorderable
              handleSearch={handleInputChange}
              handleSaveLayout={handleSaveLayout}
              isSaveLayout={true}
              gridConfigFromApi={gridConfig}
              handleResetLayout={handleResetLayout}
              reFixPageNumber
            />
          </Paper>
        </div>
      </SiTrackPage>
    </div>
  );
};
